import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { Box, Button, Typography } from '@mui/material';
import { FC, useMemo } from 'react';

import styles from './print.module.css';

import type {
  DispatchRecordGroup,
  DispatchRecordReport,
  DispatchRecordRow,
} from 'src/entities/DispatchRecord.entity';


const ROWS_PER_PAGE = 33;

type FlatItem =
  | { kind: 'row'; row: DispatchRecordRow }
  | { kind: 'separator' };

const buildAggregationKey = (row: DispatchRecordRow): string =>
  JSON.stringify([
    row.shipper_name,
    row.origin_area,
    row.dest_area,
    row.store_name,
  ]);

const aggregateRows = (rows: DispatchRecordRow[]): DispatchRecordRow[] => {
  const rowsByKey = new Map<string, DispatchRecordRow>();

  rows.forEach((row) => {
    const key = buildAggregationKey(row);
    const current = rowsByKey.get(key);

    if (!current) {
      rowsByKey.set(key, { ...row });
      return;
    }

    rowsByKey.set(key, {
      ...current,
      cases: current.cases + row.cases,
      weight: current.weight + row.weight,
    });
  });

  return Array.from(rowsByKey.values());
};

const flatten = (groups: DispatchRecordGroup[]): FlatItem[] => {
  const out: FlatItem[] = [];
  groups.forEach((g, gi) => {
    aggregateRows(g.rows).forEach((row) => out.push({ kind: 'row', row }));
    if (gi < groups.length - 1) out.push({ kind: 'separator' });
  });
  return out;
};

const paginate = (flat: FlatItem[]): FlatItem[][] => {
  const pages: FlatItem[][] = [];
  for (let i = 0; i < flat.length; i += ROWS_PER_PAGE) {
    pages.push(flat.slice(i, i + ROWS_PER_PAGE));
  }
  if (pages.length === 0) pages.push([]);
  return pages;
};

const formatIssuedAt = (iso: string): { date: string; time: string } => {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour')}:${get('minute')}`,
  };
};

type Props = {
  startOn: string;
  endOn: string;
  data?: DispatchRecordReport;
  isLoading: boolean;
  isError: boolean;
};

const DispatchRecordReportPresenter: FC<Props> = ({ startOn, endOn, data, isLoading, isError }) => {
  const pagedDays = useMemo(() => {
    if (!data) return [];
    return data.days.map((day) => ({
      delivery_date: day.delivery_date,
      pages: paginate(flatten(day.groups)),
    }));
  }, [data]);

  const issued = data ? formatIssuedAt(data.issued_at) : { date: '', time: '' };
  const totalPages = pagedDays.reduce((n, d) => n + d.pages.length, 0);
  let pageNumber = 0;

  return (
    <>
      <title>配車表 ({startOn}{startOn !== endOn ? ` 〜 ${endOn}` : ''})</title>
      <div className={styles.shell} role="region" aria-label="配車表 プレビュー">
        <div className={styles.toolbar}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={() => window.close()}>
            戻る
          </Button>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            配車表
          </Typography>
          <Box className={styles.toolbarRight}>
            <Typography variant="caption" color="text.secondary">
              全 {Math.max(totalPages, 1)} ページ
            </Typography>
            <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={() => window.print()}>
              印刷
            </Button>
          </Box>
        </div>

        <div className={styles.body}>
          {isLoading && <div className={styles.empty}>読み込み中...</div>}
          {!isLoading && isError && <div className={styles.empty}>エラーが発生しました。</div>}
          {!isLoading && !isError && (!data || data.days.length === 0) && (
            <div className={styles.empty}>該当する配車記録はありません。</div>
          )}
          {pagedDays.map((day) =>
            day.pages.map((pageItems, idx) => {
              pageNumber += 1;
              return (
                <div className={styles.reportPage} key={`${day.delivery_date}-${idx}`}>
                  <div className={styles.header}>
                    <div className={styles.deliveryDate}>{day.delivery_date}</div>
                    <div className={styles.title}>配車表</div>
                    <div className={styles.meta}>
                      発行日付: {issued.date}<br />
                      発行時刻: {issued.time}<br />
                      ページ: {pageNumber} / {totalPages}
                    </div>
                  </div>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th style={{ width: '10%' }}>荷主名</th>
                        <th style={{ width: '9%' }}>積地エリア</th>
                        <th style={{ width: '9%' }}>降地エリア</th>
                        <th style={{ width: '19%' }}>納品先名</th>
                        <th style={{ width: '5%' }}>数量</th>
                        <th style={{ width: '6%' }}>重量</th>
                        <th style={{ width: '13%' }}>備考</th>
                        <th style={{ width: '4%' }}>屯数</th>
                        <th style={{ width: '9%' }}>運送会社名</th>
                        <th style={{ width: '7%' }}>車両番号</th>
                        <th style={{ width: '9%' }}>ドライバー名</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.map((item, i) => {
                        if (item.kind === 'separator') {
                          return (
                            <tr key={`sep-${i}`} className={styles.separator}>
                              <td colSpan={11} />
                            </tr>
                          );
                        }
                        const r = item.row;
                        return (
                          <tr key={i}>
                            <td>{r.shipper_name}</td>
                            <td>{r.origin_area}</td>
                            <td>{r.dest_area}</td>
                            <td>{r.store_name}</td>
                            <td className={styles.num}>{r.cases}</td>
                            <td className={styles.num}>
                              {r.weight.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                            </td>
                            <td>{r.remarks}</td>
                            <td className={styles.center}>{r.tonnage}</td>
                            <td>{r.company_name}</td>
                            <td className={styles.center}>{r.vehicle_number}</td>
                            <td>{r.driver_name}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            }),
          )}
        </div>
      </div>
    </>
  );
};

export default DispatchRecordReportPresenter;
